// BULK SAVE TO PROJECT (from CV search) **********************************************************
// pathname: /v1/resume-operation/bulk-save-to-project
// ************************************************************************************************
import { useSwrMutationApi } from 'src/hooks/useSwrApi';
import { BulkSaveToProjectResponse, PostBulkSaveToProjectObject } from '@type/v1-api-types';

/**
 * @description Bulk save-to-project — from `/cv/search`. The recruiter picks
 * an existing job (jobId/jobSlug) or provides a new title; the backend
 * resolves or creates the job, then turns each selected candidate into an
 * Applicant. No status transition, no interview-request pipeline — the
 * recruiter picks a status later from the project's applicant list.
 *
 * Response counts per bucket: `succeeded`, `promoted`, `alreadyApplicant`,
 * `skippedUnknown`, `failed`, plus the resolved `jobId` / `jobSlug` /
 * `jobTitle`.
 *
 * @param locale - the locale
 * @param onSuccess - callback with the typed response so the caller can render per-bucket counts
 * @param onError - callback on error
 *
 * @example
 * const { trigger, isMutating } = useSwrBulkSaveToProject({ locale: 'ro' });
 * trigger({ jobId: 123, userSlugs: ['a', 'b'] });
 * // or, create a new job
 * trigger({ jobTitle: 'Backend Dev', userSlugs: ['a', 'b'] });
 */
export const useSwrBulkSaveToProject = (props: {
  locale?: string;
  onSuccess?: (response: BulkSaveToProjectResponse | undefined) => void;
  onError?: () => void;
}) => {
  const {
    locale = 'ro',
    onSuccess,
    onError,
  } = props;

  const {
    data,
    error,
    trigger,
    isMutating,
    reset,
  } = useSwrMutationApi<BulkSaveToProjectResponse, PostBulkSaveToProjectObject>({
    url: '/v1/resume-operation/bulk-save-to-project',
    locale,
    fetcherConfig: {
      method: 'POST',
      stringifyPayload: true,
      headers: {
        'Content-Type': 'application/json',
      },
    },
    apiOptions: {
      onSuccess: (response) => {
        if (onSuccess) onSuccess(response);
      },
      onError: () => {
        if (onError) onError();
      },
    },
    toastsOptions: {
      showSuccessToast: false,
      showErrorToast: true,
    },
  });

  return {
    data,
    error,
    trigger,
    isMutating,
    reset,
  };
};
